<template>
  <div class="dashboard-page">
    <!-- Section 1: Banner -->
    <section class="banner-section">
      <div class="container-fluid px-0">
        <BannerSlide
          :auto-play="true"
          :interval="5000"
          :show-indicators="true"
        />
      </div>
    </section>

    <!-- Section 2: EXP/Level (Kaiju Themed) -->
    <section class="exp-section">
      <ExpCard />
    </section>

    <!-- Section 3: Shortcuts -->
    <section class="shortcuts-section">
      <ShortcutGrid
        :items="shortcutItems"
        @shortcut-click="handleShortcutClick"
      />
    </section>

    <!-- Free Diamond Modal -->
    <FreeDiamondModal
      v-model="showFreeDiamondModal"
      :diamond-data="diamondData"
      @claimed="onDiamondClaimed"
      @refresh="checkDiamondEligibility"
    />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BannerSlide from './component/BannerSlide.vue'
import ExpCard from './component/ExpCard.vue'
import ShortcutGrid from './component/ShortcutGrid.vue'
import FreeDiamondModal from './component/FreeDiamondModal.vue'

export default {
  name: 'DashboardPage',
  components: {
    BannerSlide,
    ExpCard,
    ShortcutGrid,
    FreeDiamondModal
  },
  data() {
    return {
      showFreeDiamondModal: false,
      // Diamond eligibility data (fetched on mount)
      diamondData: {
        canClaim: false,
        diamondsAvailable: 0,
        hoursRemaining: 0,
        minutesRemaining: 0,
        secondsRemaining: 0,
        nextClaimTime: null
      },
      // Shortcut items (badge: true for dot, badge: number for count)
      shortcutItems: [
        { icon: '🎡', label: 'กงล้อ', action: 'wheel', badge: null },
        {
          icon: '👥',
          label: 'ชวนเพื่อน',
          action: 'referral',
          badge: null
        },
        { icon: '✅', label: 'เช็คอิน', action: 'checkin', badge: null },
        { icon: '🎁', label: 'โปรโมชั่น', action: 'promotions' },
        {
          icon: '🎫',
          label: 'คูปองหวย',
          action: 'lottery-coupon',
          badge: null
        },
        {
          icon: '💎',
          label: 'รับเพชรฟรี',
          action: 'free-diamond',
          isComingSoon: false,
          badge: null
        },
        { icon: '👤', label: 'ข้อมูลส่วนตัว', action: 'personal-info' },
        {
          icon: '🔑',
          label: 'กรอก code',
          action: 'redeem',
          isComingSoon: true
        }
      ]
    }
  },
  computed: {
    ...mapGetters('member', ['diamondBalance']),
    ...mapState('checkin', ['currentStreak', 'canClaim']),

    // Calculate available spins from diamond balance (100 diamonds per spin)
    wheelSpinBadge() {
      const spins = Math.floor(this.diamondBalance / 100)
      return spins > 0 ? spins : null
    },

    // Check-in badge showing progress (0/3, 1/3, 2/3, 3/3)
    checkinBadge() {
      const streak = this.currentStreak || 0
      let variant = 'muted'
      if (this.canClaim) {
        variant = 'success'
      } else if (streak > 0) {
        variant = 'warning'
      }
      return {
        text: `${streak}/3`,
        variant
      }
    }
  },
  watch: {
    // Update wheel badge when diamond balance changes
    wheelSpinBadge: {
      immediate: true,
      handler(newVal) {
        const index = this.shortcutItems.findIndex(
          item => item.action === 'wheel'
        )
        if (index !== -1) {
          this.$set(this.shortcutItems[index], 'badge', newVal)
        }
      }
    },
    // Update check-in badge when currentStreak or canClaim changes
    currentStreak: {
      immediate: true,
      handler() {
        this.updateCheckinBadge()
      }
    },
    canClaim: {
      immediate: true,
      handler() {
        this.updateCheckinBadge()
      }
    }
  },
  mounted() {
    this.checkDiamondEligibility()
    this.updateWheelBadge()
    this.fetchCheckinStatus()
    this.fetchCouponLottoStatus()
  },
  methods: {
    ...mapActions('checkin', ['fetchStatus']),

    updateWheelBadge() {
      const index = this.shortcutItems.findIndex(
        item => item.action === 'wheel'
      )
      if (index !== -1) {
        this.$set(this.shortcutItems[index], 'badge', this.wheelSpinBadge)
      }
    },

    async fetchCheckinStatus() {
      try {
        await this.fetchStatus({ force: true })
        this.updateCheckinBadge()
      } catch (error) {
        console.error('Error fetching check-in status:', error)
      }
    },

    updateCheckinBadge() {
      const index = this.shortcutItems.findIndex(
        item => item.action === 'checkin'
      )
      if (index !== -1) {
        this.$set(this.shortcutItems[index], 'badge', this.checkinBadge)
      }
    },
    handleShortcutClick(item) {
      console.log('Shortcut clicked:', item.action)

      // Check for coming soon items first
      if (item.isComingSoon) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'พบกับระบบใหม่เร็วๆนี้',
            icon: 'ClockIcon',
            variant: 'info'
          }
        })
        return
      }

      // Handle different shortcut actions
      switch (item.action) {
        case 'wheel':
          this.$router.push('/spin-wheel').catch(() => {})
          break
        case 'referral':
          this.$router.push('/referral').catch(() => {})
          break
        case 'checkin':
          // Navigate to check-in page
          this.$router.push('/checkin').catch(() => {})
          break
        case 'lottery-coupon':
          this.$router.push('/coupon-lotto').catch(() => {})
          break
        case 'free-diamond':
          this.showFreeDiamondModal = true
          break
        case 'redeem':
          // Open redeem code modal
          this.$root.$emit('show-redeem-modal')
          break
        case 'promotions':
          this.$router.push('/promotion')
          break
        case 'personal-info':
          this.$router.push('/personal-info').catch(() => {})
          break
        default:
          console.warn('Unknown shortcut action:', item.action)
      }
    },
    async checkDiamondEligibility() {
      try {
        const response = await this.$http.get(
          '/member/diamond/check-daily-free'
        )
        if (response.data.success) {
          const { data } = response.data
          this.diamondData = {
            canClaim: data.can_claim,
            diamondsAvailable: data.diamonds_available,
            hoursRemaining: data.hours_remaining,
            minutesRemaining: data.minutes_remaining,
            secondsRemaining: data.seconds_remaining,
            nextClaimTime: data.next_claim_time
          }
          // Update badge on shortcut item
          this.updateDiamondBadge()
        }
      } catch (error) {
        console.error('Error checking diamond eligibility:', error)
      }
    },
    updateDiamondBadge() {
      const diamondItem = this.shortcutItems.find(
        item => item.action === 'free-diamond'
      )
      if (diamondItem) {
        // Show badge (red dot) when can claim
        diamondItem.badge = this.diamondData.canClaim ? true : null
      }
    },
    onDiamondClaimed() {
      // Refresh eligibility after claim
      this.checkDiamondEligibility()
      // Refresh balance if eventBus is available
      if (this.$eventBus) {
        this.$eventBus.$emit('refreshBalance')
      }
    },
    async fetchCouponLottoStatus() {
      try {
        const response = await this.$http.get('/member/coupon-lotto/status')
        if (response.data.success) {
          const { coupons } = response.data.data
          this.updateCouponLottoBadge(coupons?.available || 0)
        }
      } catch (error) {
        // Silently fail - feature may not be enabled yet
        console.debug('Coupon lotto status not available:', error.message)
      }
    },
    updateCouponLottoBadge(count) {
      const index = this.shortcutItems.findIndex(
        item => item.action === 'lottery-coupon'
      )
      if (index !== -1) {
        // Show count badge if coupons available, null otherwise
        this.$set(this.shortcutItems[index], 'badge', count > 0 ? count : null)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Mobile-First styles (375px base)
.dashboard-page {
  min-height: 100vh;
  padding-bottom: 100px; // Space for bottom navigation
}

.banner-section {
  margin-bottom: 16px;

  @media (min-width: 768px) {
    margin-bottom: 24px;
  }
}

.exp-section {
  margin-bottom: 20px;

  @media (min-width: 768px) {
    margin-bottom: 28px;
  }
}

.shortcuts-section {
  margin-bottom: 20px;

  @media (min-width: 768px) {
    margin-bottom: 28px;
  }
}
</style>
